import { useContext, useEffect, useMemo, useRef } from 'react';

import { LazyQueryHookOptions, LazyQueryResultTuple } from '@apollo/client';
import { Button, Icon } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { AiFillCalculator } from 'react-icons/ai';
import { toast } from 'react-toastify';

import { Deal } from '../../../../gql/dealGql';
import { TireWheelRatesQueryVariables } from '../../../../gql/generated/graphql';

import Tooltip from '../../../shared/Tooltip';

import { PermissionEnum } from '../../../../constants/permissions';
import { Can } from '../../../../libs/Can';
import { DealContext } from '../../../../libs/DealContext';
import { isNumber } from '../../../../libs/utils';

type RateResult = {
  dealer_cost?: string | null;
  retail_price?: string | null;
};

export type ProductButtonConfig<TData> = {
  permission: PermissionEnum;
  buttonText: string;
  loadingText: string;
  successMessage: string;
  errorMessage: string;
  costField: string;
  priceField: string;
  useRatesQuery: (
    options?: LazyQueryHookOptions<TData, TireWheelRatesQueryVariables>,
  ) => LazyQueryResultTuple<TData, TireWheelRatesQueryVariables>;
  extractRate: (data: TData) => RateResult | undefined;
};

type CalcProductButtonProps<TData> = {
  config: ProductButtonConfig<TData>;
  onRatesLoaded?: () => void;
};

const CalcProductButton = <TData,>({ config, onRatesLoaded }: CalcProductButtonProps<TData>) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext<Deal>();
  const { setUserChangedSomething } = useContext(DealContext);

  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const updateField = (name: string, value: unknown) => {
    setFieldValue(name, value);
    setTimeout(() => {
      if (mountedRef.current) {
        setFieldTouched(name, true);
      }
    });
  };

  const [loadRates, { loading }] = config.useRatesQuery({
    fetchPolicy: 'no-cache',
    onCompleted: (data: TData) => {
      const rate = config.extractRate(data);
      if (rate) {
        updateField(config.costField, +(rate.dealer_cost ?? ''));
        updateField(config.priceField, +(rate.retail_price ?? ''));
        toast.success(config.successMessage);
        onRatesLoaded?.();
      } else {
        toast.error(config.errorMessage);
      }
    },
    onError: () => {
      // Always show the product-specific message — upstream `error.message`
      // is unreliable (PEN occasionally returns no message and we end up
      // toasting "Error message not found"). `config.errorMessage` already
      // names the product (e.g. "Failed to retrieve Tire & Wheel rate").
      toast.error(config.errorMessage);
    },
  });

  const missingInfoErrors = useMemo(
    () => [
      !values.car.year ? 'Car Year' : '',
      !values.car.vin ? 'VIN' : '',
      !values.car.mileage ? 'Mileage' : '',
      !isNumber(values.car.retail_book_value) ? 'KBB Retail' : '',
      !isNumber(values.car.payoff.vehicle_payoff) ? 'Payoff' : '',
      !isNumber(values.financial_info.sell_rate) ? 'Sell Rate' : '',
      !isNumber(values.financial_info.term) ? 'Term' : '',
      !values.customer.address?.state ? 'State' : '',
    ],
    [
      values.car.year,
      values.car.vin,
      values.car.mileage,
      values.car.retail_book_value,
      values.car.payoff.vehicle_payoff,
      values.financial_info.sell_rate,
      values.financial_info.term,
      values.customer.address?.state,
    ],
  );

  const missingInfo = missingInfoErrors.some(Boolean);

  return (
    <Can I={config.permission}>
      <Tooltip errors={missingInfoErrors.filter(Boolean)}>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Icon as={AiFillCalculator} fontSize="md" />}
          isLoading={loading}
          loadingText={config.loadingText}
          isDisabled={missingInfo}
          onClick={() => {
            setUserChangedSomething(true);

            loadRates({
              variables: {
                bank: values.financial_info.bank ?? '',
                deal_id: values.id ?? 0,
                data: {
                  year: parseInt(values.car.year, 10),
                  vin: values.car.vin,
                  mileage: values.car.mileage,
                  msrp: values.car.book_value,
                  purchasePrice: values.car.payoff.vehicle_payoff,
                  apr: values.financial_info.sell_rate,
                  financedAmount: values.financial_info.amount_financed,
                  financeTerms: values.financial_info.term,
                  financeTermsMileage: 0,
                  state: values.customer.address?.state,
                },
              },
            });
          }}
        >
          {config.buttonText}
        </Button>
      </Tooltip>
    </Can>
  );
};

export default CalcProductButton;
